import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle, XCircle, Clock, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../api';

const STATUS_CONFIG = {
  present: { label: 'Present', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: CheckCircle },
  absent:  { label: 'Absent',  color: 'text-red-600',     bg: 'bg-red-50',     border: 'border-red-200',     dot: 'bg-red-500',     icon: XCircle },
  late:    { label: 'Late',    color: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-200',   dot: 'bg-amber-500',   icon: Clock },
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function AttendancePage() {
  const now = new Date();
  const [year, setYear]     = useState(now.getFullYear());
  const [month, setMonth]   = useState(now.getMonth() + 1);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ total: 0, present: 0, absent: 0, late: 0, rate: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/students/my-attendance?month=${month}&year=${year}`)
      .then(r => {
        if (r?.success) {
          setRecords(r.data || []);
          setSummary(r.summary || { total: 0, present: 0, absent: 0, late: 0, rate: 0 });
        }
      })
      .finally(() => setLoading(false));
  }, [month, year]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const recordMap = Object.fromEntries(records.map(r => [r.date?.split('T')[0], r]));

  const rateColor = summary.rate >= 90 ? 'text-emerald-600' : summary.rate >= 75 ? 'text-amber-600' : 'text-red-600';
  const rateRing  = summary.rate >= 90 ? '#10b981' : summary.rate >= 75 ? '#f59e0b' : '#ef4444';

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-slate-800">Attendance</h1>
        <p className="text-slate-500 text-sm mt-0.5">Track your daily school attendance</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="attendance-summary">
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
          <p className="text-xs text-slate-500 font-medium mb-1">Total Days</p>
          <p className="text-2xl font-black text-slate-800" data-testid="attendance-total">{summary.total}</p>
        </div>
        <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100 shadow-sm">
          <p className="text-xs text-emerald-600 font-medium mb-1">Present</p>
          <p className="text-2xl font-black text-emerald-700" data-testid="attendance-present">{summary.present}</p>
        </div>
        <div className="bg-red-50 rounded-2xl p-4 border border-red-100 shadow-sm">
          <p className="text-xs text-red-600 font-medium mb-1">Absent</p>
          <p className="text-2xl font-black text-red-700" data-testid="attendance-absent">{summary.absent}</p>
        </div>
        <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100 shadow-sm">
          <p className="text-xs text-amber-600 font-medium mb-1">Late</p>
          <p className="text-2xl font-black text-amber-700" data-testid="attendance-late">{summary.late}</p>
        </div>
      </div>

      {/* Attendance Rate */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex items-center gap-6">
        <div className="relative w-20 h-20 flex-shrink-0">
          <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="15.9" fill="none"
              stroke={rateRing} strokeWidth="3"
              strokeDasharray={`${summary.rate} ${100 - summary.rate}`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-lg font-black ${rateColor}`}>{summary.rate}%</span>
          </div>
        </div>
        <div>
          <p className="font-black text-slate-800 text-lg">Attendance Rate</p>
          <p className="text-slate-500 text-sm">
            {summary.rate >= 90 ? 'Excellent! Keep it up.' : summary.rate >= 75 ? 'Good, aim for 90%+.' : 'Needs improvement — try to attend regularly.'}
          </p>
          <div className="flex items-center gap-1.5 mt-2">
            <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs text-slate-400">{MONTHS[month - 1]} {year}</span>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="attendance-calendar">
        {/* Month Navigation */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <button onClick={prevMonth} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" data-testid="attendance-prev-month">
            <ChevronLeft className="w-4 h-4 text-slate-600" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-500" />
            <span className="font-black text-slate-800">{MONTHS[month - 1]} {year}</span>
          </div>
          <button onClick={nextMonth} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" data-testid="attendance-next-month">
            <ChevronRight className="w-4 h-4 text-slate-600" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-px bg-slate-100 border-b border-slate-100">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} className="bg-white text-center py-2 text-xs font-bold text-slate-400">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        {loading ? (
          <div className="py-16 text-center text-slate-400 text-sm">Loading attendance…</div>
        ) : (
          <div className="grid grid-cols-7 gap-px bg-slate-100">
            {Array.from({ length: firstDay }, (_, i) => (
              <div key={`empty-${i}`} className="bg-white min-h-[56px]" />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dateKey = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const rec = recordMap[dateKey];
              const cfg = rec ? STATUS_CONFIG[rec.status] : null;
              const isToday = dateKey === now.toISOString().split('T')[0];
              return (
                <div
                  key={day}
                  className={`bg-white min-h-[56px] p-2 flex flex-col items-center gap-1 ${isToday ? 'ring-2 ring-inset ring-blue-400' : ''}`}
                >
                  <span className={`text-xs font-bold ${isToday ? 'text-blue-600' : 'text-slate-600'}`}>{day}</span>
                  {cfg && (
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                      {cfg.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Records List */}
      {records.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-black text-slate-800">Recent Records</h2>
          </div>
          <div className="divide-y divide-slate-50" data-testid="attendance-records-list">
            {records.slice(0, 20).map((rec, idx) => {
              const cfg = STATUS_CONFIG[rec.status] || STATUS_CONFIG.present;
              const StatusIcon = cfg.icon;
              return (
                <div key={idx} className="flex items-center gap-3 px-5 py-3">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${cfg.bg}`}>
                    <StatusIcon className={`w-4 h-4 ${cfg.color}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-800">
                      {new Date(rec.date).toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </p>
                    {rec.note && <p className="text-xs text-slate-500 mt-0.5">{rec.note}</p>}
                  </div>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                    {cfg.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && records.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center" data-testid="attendance-empty">
          <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-bold text-slate-500">No attendance records yet</p>
          <p className="text-sm text-slate-400 mt-1">Records will appear once your teacher takes attendance</p>
        </div>
      )}
    </div>
  );
}
